import { useConnection } from './store/connection';

const BASE = process.env.NEXT_PUBLIC_API_URL ?? '/api';

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message);
  }
}

// ── Core fetch ─────────────────────────────────────────────────
// Auth is handled by the splace_session cookie (httpOnly, set by the
// backend on verify-otp / register, cleared on logout). The browser
// attaches it automatically as long as we send credentials: 'include'.
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...socketIdHeader(),
    ...(init.headers as Record<string, string>),
  };

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  return parseResponse<T>(res);
}

/**
 * Multipart upload variant. Skips the JSON Content-Type header so the
 * browser writes its own with the multipart boundary.
 */
async function uploadRequest<T>(path: string, body: FormData): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    body,
    headers: socketIdHeader(),
    credentials: 'include',
  });
  return parseResponse<T>(res);
}

/**
 * Read the live socket.io connection id from the connection store and
 * pack it into an `X-Socket-Id` header. The BE uses it to skip the
 * originating socket when emitting the resulting event so we don't
 * process the change twice on this device.
 */
function socketIdHeader(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const id = useConnection.getState().socketId;
  return id ? { 'X-Socket-Id': id } : {};
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.message ?? res.statusText, body.code);
  }
  if (res.status === 204) return undefined as T;
  // Backend wraps every JSON in `{ data, time }` (commit bee1932). `time`
  // is server-side epoch-ms — record it so countdowns can anchor on the
  // server's clock instead of Date.now().
  const body = await res.json();
  if (body && typeof body === 'object' && 'time' in body && 'data' in body) {
    recordServerTime(body.time as number);
    return body.data as T;
  }
  return body as T;
}

// ── Server clock tracking ──────────────────────────────────────────
// Every response carries the server's `time`. We track the offset
// between server and client clocks so any countdown can read
// `serverNow()` instead of `Date.now()` and stay correct even on
// devices with a wrong clock or different timezone.

let serverClockOffsetMs = 0;
// Last absolute server time we observed. Distinct from `serverNow()`,
// which extrapolates forward via the local clock — this is the floor
// the reconnect catch-up uses for `?updatedAfter=<epochMs>` so it
// never skips events that happened during the disconnect window.
let lastServerTime = 0;
export function recordServerTime(serverMs: number) {
  serverClockOffsetMs = serverMs - Date.now();
  if (serverMs > lastServerTime) lastServerTime = serverMs;
}
export function serverNow(): number {
  return Date.now() + serverClockOffsetMs;
}
export function getLastServerTime(): number {
  return lastServerTime;
}

/**
 * Effective deadline of an avatar in epoch-ms. `expiredAt` is the
 * canonical "no longer alive" stamp (written by the BE cron on
 * natural expiry and by explicit endAvatar) so it's the first pick
 * when set. `deletedAt` is the next stamp (only after expiredAt by
 * BE rules). Falls back to the natural end while the avatar is
 * still alive.
 */
export function avatarDeadline(a: {
  createdAt: number;
  duration: number;
  expiredAt: number | null;
  deletedAt: number | null;
}): number {
  return a.expiredAt ?? a.deletedAt ?? a.createdAt + a.duration * 1000;
}

/**
 * Mirror of the backend `isAvatarAlive`. True iff `expiredAt` and
 * `deletedAt` are both null AND the natural deadline hasn't passed.
 * `endedAt` (informational: "user ended" vs "naturally expired") is
 * deliberately ignored — every "ended" path also stamps `expiredAt`,
 * so the live/dead question reduces to those two fields plus the
 * natural deadline. Reads `serverNow()` directly so the result
 * advances on its own when the calling component re-renders.
 */
export function isAvatarAlive(a: {
  createdAt: number;
  duration: number;
  expiredAt: number | null;
  deletedAt: number | null;
}): boolean {
  if (a.expiredAt || a.deletedAt) return false;
  return a.createdAt + a.duration * 1000 > serverNow();
}

/**
 * Compact relative time for chat lists: now / Nm / Nh / Nd / Nw / Mon D.
 * Uses `serverNow()` so the value is drift-correct regardless of the
 * device clock. Returns an empty string when the timestamp is missing
 * — the caller is responsible for whatever placeholder it wants to
 * show in that case.
 */
export function formatActivity(activityAt: number | null | undefined): string {
  if (!activityAt || !Number.isFinite(activityAt)) return '';
  const diff = Math.max(0, serverNow() - activityAt);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w`;
  return new Date(activityAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Day-header label for chat day separators.
 * Input is the message `createdAt` in microseconds (server units).
 * Returns Today / Yesterday / weekday (within last week) / "D MMM YYYY".
 */
export function formatChatDay(createdAtMicros: number): string {
  const d   = new Date(createdAtMicros / 1000);
  const now = new Date(serverNow());
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7)   return d.toLocaleDateString('en', { weekday: 'long' });
  return d.toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Stable per-local-day key for grouping consecutive messages. */
export function chatDayKey(createdAtMicros: number): string {
  const d = new Date(createdAtMicros / 1000);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

// ── API surface ────────────────────────────────────────────────

export const api = {

  auth: {
    sendOtp: (email: string) =>
      request<Record<string, never>>('/auth/send-otp', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    verifyOtp: (email: string, code: string) =>
      request<{ isNewUser: boolean; user: User }>(
        '/auth/verify-otp',
        { method: 'POST', body: JSON.stringify({ email, code }) },
      ),

    google: (idToken: string) =>
      request<{ isNewUser: boolean; user: User }>(
        '/auth/google',
        { method: 'POST', body: JSON.stringify({ idToken }) },
      ),

    logout: () =>
      request<Record<string, never>>('/auth/logout', { method: 'POST' }),
  },

  users: {
    me: () => request<User>('/users/me'),
    update: (body: Partial<{ username: string; displayName: string; bio: string; photoUrl: string }>) =>
      request<User>('/users/me', { method: 'PATCH', body: JSON.stringify(body) }),
    checkUsername: (candidate: string) =>
      request<{ available: boolean }>(`/users/check-username?u=${encodeURIComponent(candidate)}`),
    byUsername: (username: string) =>
      request<User>(`/users/username/${encodeURIComponent(username)}`),
    uploadPhoto: (file: File) => {
      const fd = new FormData();
      fd.append('photo', file);
      // Don't set Content-Type — the browser builds the multipart boundary.
      return uploadRequest<User>('/users/me/photo', fd);
    },
  },

  avatars: {
    create: (body: { name: string; bio?: string; tags?: string; durationSeconds: number }) =>
      request<Avatar>('/avatars', { method: 'POST', body: JSON.stringify(body) }),
    me: () => request<Avatar | null>('/avatars/me'),
    history: (opts?: { next?: string; prev?: string; updatedAfter?: number }) => {
      const qs = paginationQS(opts);
      return request<Paginated<Avatar, string>>(`/avatars/history${qs}`);
    },
    deactivate: () => request<Avatar | null>('/avatars/me', { method: 'DELETE' }),
    delete: (id: string) => request<Avatar>(`/avatars/${id}`, { method: 'DELETE' }),
    get: (id: string) => request<Avatar>(`/avatars/${id}`),
    sessions: (avatarId: string, opts?: { next?: string; prev?: string }) => {
      const qs = paginationQS(opts);
      return request<Paginated<Session, string>>(`/avatars/${avatarId}/sessions${qs}`);
    },
    uploadPhoto: (avatarId: string, file: File) => {
      const fd = new FormData();
      fd.append('photo', file);
      return uploadRequest<Avatar>(`/avatars/${avatarId}/photo`, fd);
    },
  },

  feed: {
    get: (opts?: { next?: string; prev?: string }) => {
      const qs = paginationQS(opts);
      return request<Paginated<FeedAvatar, string>>(`/feed${qs}`);
    },
  },

  sessions: {
    // Probe: returns the avatar plus the existing session if one is open
    // between this seeker and this avatar. No DB write — the session is
    // created lazily by `startMessage` on the first send.
    open: (avatarId: string) =>
      request<{
        avatar: Avatar;
        session: Session | null;
        // Set when an active link already exists between seeker and
        // avatar owner. Tells the FE to render the read-only "you're
        // linked" view (no session is created in this case).
        linked: { linkId: string; linkedAt: number } | null;
      }>(`/sessions/open/${avatarId}`, { method: 'POST' }),
    seekerMine: (opts?: { next?: string; prev?: string; updatedAfter?: number }) => {
      const qs = paginationQS(opts);
      return request<Paginated<Session, string>>(`/sessions/mine/seeker${qs}`);
    },
    avatarMine: (opts?: { next?: string; prev?: string; updatedAfter?: number }) => {
      const qs = paginationQS(opts);
      return request<Paginated<Session, string>>(`/sessions/mine/avatar${qs}`);
    },
    get: (id: string) => request<Session>(`/sessions/${id}`),
    // Lazy first send by avatarId — backend upserts the session and
    // inserts the message in one transaction.
    startMessage: (avatarId: string, content: MessageContent) =>
      request<{ session: Session; message: SessionMessage }>(
        `/sessions/avatar/${avatarId}/messages`,
        { method: 'POST', body: JSON.stringify({ content }) },
      ),
    sendMessage: (id: string, content: MessageContent) =>
      request<{ session: Session; message: SessionMessage }>(
        `/sessions/${id}/messages`,
        { method: 'POST', body: JSON.stringify({ content }) },
      ),
    getMessages: (id: string, opts?: { next?: number; prev?: number }) => {
      const qs = paginationQS(opts);
      return request<{ session: Session; messages: SessionMessage[]; next: number | null; prev: number | null }>(
        `/sessions/${id}/messages${qs}`,
      );
    },
    delete: (id: string) => request<Session>(`/sessions/${id}`, { method: 'DELETE' }),
    restore: (id: string) => request<Session>(`/sessions/${id}/restore`, { method: 'POST' }),

    // Link request lives inline on the session row (`linkRequest` is
    // 'PENDING' or null). Always seeker → avatar; the avatar owner
    // patches to accept/reject. All three return the decorated session
    // (`respond` accept also returns the per-side decorated link).
    sendRequest: (sessionId: string) =>
      request<Session | null>(`/sessions/${sessionId}/request`, { method: 'POST' }),
    revokeRequest: (sessionId: string) =>
      request<Session | null>(`/sessions/${sessionId}/request`, { method: 'DELETE' }),
    respondRequest: (sessionId: string, accept: boolean) =>
      request<{ session: Session | null; link: Link | null; user?: User }>(
        `/sessions/${sessionId}/request`, { method: 'PATCH', body: JSON.stringify({ accept }) },
      ),
  },

  links: {
    mine: (opts?: { next?: string; prev?: string; updatedAfter?: number }) => {
      const qs = paginationQS(opts);
      return request<Paginated<Link, string>>(`/links${qs}`);
    },
    get: (id: string) => request<Link>(`/links/${id}`),
    cut: (id: string) => request<{ link: Link | null; user: User }>(`/links/${id}`, { method: 'DELETE' }),
    sendMessage: (id: string, content: MessageContent) =>
      request<{ link: Link; message: LinkedMessage }>(
        `/links/${id}/messages`,
        { method: 'POST', body: JSON.stringify({ content }) },
      ),
    sendPhoto: (id: string, file: File) => {
      const fd = new FormData();
      fd.append('photo', file);
      return uploadRequest<{ link: Link; message: LinkedMessage }>(
        `/links/${id}/messages/photo`, fd,
      );
    },
    getMessages: (id: string, opts?: { next?: number; prev?: number }) => {
      const qs = paginationQS(opts);
      return request<{ messages: LinkedMessage[]; next: number | null; prev: number | null }>(
        `/links/${id}/messages${qs}`,
      );
    },
  },
};

/**
 * Paginated response envelope used by every list endpoint. `next` and
 * `prev` are ready-to-use cursor values: feed `next` straight back as
 * the `next` query param to fetch the page after this one (older
 * items), and `prev` to fetch newer items / refresh. Null in either
 * field means "no more in that direction".
 */
export interface Paginated<T, C> {
  items: T[];
  next: C | null;
  prev: C | null;
}

function paginationQS(opts?: { next?: string | number; prev?: string | number; updatedAfter?: number }): string {
  if (!opts) return '';
  // `updatedAfter` is mutually exclusive with cursor pagination — used
  // for the reconnect catch-up. BE returns every row the caller can see
  // with `updatedAt > param` regardless of visibility filters; the FE
  // routes each row through the matching `processMy*` writer.
  if (opts.updatedAfter !== undefined) return `?updatedAfter=${opts.updatedAfter}`;
  if (opts.next !== undefined && opts.next !== null) return `?next=${opts.next}`;
  if (opts.prev !== undefined && opts.prev !== null) return `?prev=${opts.prev}`;
  return '';
}

// ── Types ──────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  username: string;
  displayName: string;
  bio?: string;
  photoUrl?: string;
  photoThumbUrl?: string;
  countLinks: number;
  createdAt: string;
}

export const USERNAME_REGEX = /^[a-z0-9_][a-z0-9_.]{1,28}[a-z0-9_]$/;

export interface Avatar {
  id: string;
  name: string;
  imageUrl?: string;
  bio?: string;
  tags?: string;
  // Lifetime model. `expiredAt` is the canonical "no longer alive"
  // stamp — both the BE 1Hz reaper (natural deadline) and explicit
  // endAvatar (user clicked End) write it. `endedAt` is informational
  // only: it's also set when the termination was user-driven, so
  // clients can distinguish "ended on purpose" from "naturally
  // expired" (e.g. for a Memories badge). `deletedAt` is the hard
  // delete from the Memories grid; per BE rules it can only be set
  // after `expiredAt`. Liveness check is `!expiredAt && !deletedAt &&
  // createdAt + duration > serverNow()` — see `isAvatarAlive`.
  createdAt: number;    // epoch-ms
  duration: number;     // seconds
  endedAt:   number | null;   // epoch-ms, set on explicit user end
  expiredAt: number | null;   // epoch-ms, set on natural deadline OR explicit end
  deletedAt: number | null;   // epoch-ms, set when hard-deleted
}

export type FeedAvatar = Avatar;

export interface Session {
  id: string;
  seekerId: string;
  avatarId: string;
  createdAt: number;
  // Canonical "no longer alive" stamp. Set by the BE reaper in lockstep
  // with avatar.expiredAt (cron path: avatar's natural deadline;
  // explicit endAvatar path: the moment of termination). Once set the
  // chat is read-only.
  expiredAt?: number | null;
  deletedSeekerAt?: number | null;
  // Last chat activity — bumped on every message send. NULL for a
  // session created without a first message (link-request flow).
  // Display only; sort uses `rankedAt`.
  activityAt?: number | null;
  // Sort key. Bumped on create, message, and lifecycle reopens
  // (restore, re-link). Always set. Used for inbox ordering — paginated
  // calls walk it via `?next=`/`?prev=` cursor ids.
  rankedAt: number;
  // Set when the seeker and the avatar's owner share an active link.
  // The session page uses it to render the "linked → go to link"
  // banner and `linkId` is the navigation target. Cleared by the BE
  // when either side cuts the link.
  linkedAt?: number | null;
  linkId?: string | null;
  // 'PENDING' while the seeker has asked for a link upgrade and the
  // avatar owner hasn't responded yet, null otherwise (no request,
  // revoked, rejected, or already accepted — accept is signalled by
  // `linkedAt` + `linkId`).
  linkRequest?: 'PENDING' | null;
  avatar?: Pick<Avatar, 'id' | 'name' | 'imageUrl' | 'createdAt' | 'duration' | 'endedAt' | 'expiredAt' | 'deletedAt'>;
  seeker?: Pick<User, 'id' | 'username' | 'displayName' | 'photoUrl'>;
  // Per-side read state, projected by the BE for the caller.
  // `lastMessageAt` = createdAt µs of the latest message the OTHER
  // side sent me; `lastReadAt` = my own watermark. Inbox dot:
  // `lastMessageAt > lastReadAt`.
  lastMessageAt: number;
  lastReadAt: number;
}

// Discriminated union of message contents. Today only `text` is wired
// end-to-end; image/video/audio variants slot in the same way.
export type MessageContent =
  | { type: 'text'; text: string }
  | { type: 'image'; url: string; thumbUrl?: string; width?: number; height?: number }
  | { type: 'video'; url: string; durationSeconds?: number; thumbUrl?: string }
  | { type: 'audio'; url: string; durationSeconds?: number };

export function textContent(text: string): MessageContent {
  return { type: 'text', text };
}

export interface SessionMessage {
  id: string;
  sessionId: string;
  fromSeeker: boolean;
  content: MessageContent;
  readAt?: string;
  createdAt: number;  // µs epoch from DB clock
  // FE-only optimistic flag — true while the message lives in the
  // local cache pending a server confirmation. The BE never emits
  // this; it gets replaced by the real row on the mutation response.
  _pending?: boolean;
}

export interface Link {
  id: string;
  // The decorated row is per-user — `partner` is the other user from
  // the caller's perspective. `cutByPartner` reports whether the
  // partner has left (link stays accessible, read-only). `cutByMe`
  // is always false on HTTP responses (BE filters those out for me),
  // but can be true on the `reloadYourLink` socket event sent to my
  // other tabs after I cut, so processMyLink can drop the row.
  partner?: Pick<User, 'id' | 'username' | 'displayName' | 'photoUrl'>;
  createdAt: number;          // epoch-ms
  // Last chat activity — NULL until the first message lands on a
  // freshly-created or just-re-linked link. Display only.
  activityAt?: number | null;
  // Sort key. Bumped on create, message, and re-link. Always set.
  rankedAt: number;
  cutByMe: boolean;
  cutByPartner: boolean;
  // Per-side read state, projected for the caller. `lastMessageAt`
  // is the createdAt µs of the latest message the OTHER side sent
  // me; `lastReadAt` is the highest createdAt µs I've acknowledged
  // (via the /read endpoint or implicitly by sending). Inbox dot:
  // `lastMessageAt > lastReadAt`.
  lastMessageAt: number;
  lastReadAt: number;
}

export interface LinkedMessage {
  id: string;
  linkId: string;
  senderId: string;
  content: MessageContent;
  readAt?: string;
  createdAt: number;  // µs epoch from DB clock
  // FE-only optimistic flag — true while the message lives in the
  // local cache pending a server confirmation. The BE never emits it.
  _pending?: boolean;
}
